/**
 * OpenRouterModelSelector v2 — pogrupowany, mobile-first selektor modeli.
 *
 * Sekcje (w tej kolejności):
 *   ⭐ Polecane (filtr nad pełną listą — model występuje także w swojej grupie)
 *   OpenAI / Anthropic / Google / DeepSeek / Mistral / Meta / Qwen / xAI
 *   Inference Routers
 *   Pozostałe
 *
 * Każda grupa jest zwijana/rozwijana. Stan rozwinięcia jest pamiętany w
 * trakcie życia komponentu (przeładowanie modela go nie resetuje).
 *
 * Mobile-first: po otwarciu wszystkie grupy poza ⭐ Polecane są ZWINIĘTE —
 * użytkownik widzi listę nagłówków zamiast 300 wierszy do scrollowania.
 *
 * Wyszukiwarka przeszukuje jednocześnie: name, slug, oraz nazwę producenta.
 * Wpisanie czegokolwiek auto-rozwija grupy z trafieniami.
 *
 * Backend ai_provider / openrouter API — bez zmian (props `models` to ten
 * sam `models_metadata` co wcześniej).
 */
import { useMemo, useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Search, Star, Sparkles, Brain, Globe, Atom, Wind, Infinity as InfinityIcon,
  Hexagon, Zap, Network, Box, ChevronDown, ChevronRight,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// 1. Definicja grup producentów (kolejność = kolejność wyświetlania)
// ─────────────────────────────────────────────────────────────────────────────

// Grupy + matchery po prefiksie slug (`org/model` → org). Aliasy `~org/...`
// są tutaj obsłużone (np. ~anthropic → Anthropic).
const GROUPS = [
  {
    id: "openai", label: "OpenAI",
    Icon: Sparkles, iconClass: "text-emerald-600", dotClass: "bg-emerald-500",
    matchers: (slug) => slug === "openai" || slug === "~openai",
  },
  {
    id: "anthropic", label: "Anthropic",
    Icon: Brain, iconClass: "text-orange-600", dotClass: "bg-orange-500",
    matchers: (slug) => slug === "anthropic" || slug === "~anthropic",
  },
  {
    id: "google", label: "Google",
    Icon: Globe, iconClass: "text-blue-600", dotClass: "bg-blue-500",
    matchers: (slug) => slug === "google" || slug === "~google" || slug === "gemini",
  },
  {
    id: "deepseek", label: "DeepSeek",
    Icon: Atom, iconClass: "text-violet-600", dotClass: "bg-violet-500",
    matchers: (slug) => slug === "deepseek" || slug === "~deepseek",
  },
  {
    id: "mistral", label: "Mistral",
    Icon: Wind, iconClass: "text-amber-700", dotClass: "bg-amber-700",
    matchers: (slug) => slug === "mistralai" || slug === "mistral",
  },
  {
    id: "meta", label: "Meta",
    Icon: InfinityIcon, iconClass: "text-slate-800", dotClass: "bg-slate-800",
    matchers: (slug) => slug === "meta-llama" || slug === "meta",
  },
  {
    id: "qwen", label: "Qwen",
    Icon: Hexagon, iconClass: "text-yellow-600", dotClass: "bg-yellow-500",
    matchers: (slug) => slug === "qwen" || slug === "qwen3",
  },
  {
    id: "xai", label: "xAI",
    Icon: Zap, iconClass: "text-stone-700", dotClass: "bg-stone-600",
    matchers: (slug) => slug === "x-ai" || slug === "xai",
  },
  {
    id: "routers", label: "Inference Routers",
    Icon: Network, iconClass: "text-indigo-700", dotClass: "bg-indigo-500",
    matchers: (slug) => slug === "openrouter",
  },
  {
    id: "other", label: "Pozostałe",
    Icon: Box, iconClass: "text-stone-500", dotClass: "bg-stone-400",
    matchers: () => false, // catch-all (przypisanie domyślne)
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// 2. "Polecane" — lista patternów. Match po `startsWith` na ID modelu.
// Lista jest FILTREM nad pełną listą — modele nadal pojawiają się w swoich
// grupach. Patterny muszą być stabilne (preferujemy non-preview, non-alias).
// ─────────────────────────────────────────────────────────────────────────────

const FEATURED_MATCHERS = [
  { groupId: "openai", pattern: "openai/gpt-4o-mini", exact: false },
  { groupId: "anthropic", pattern: "anthropic/claude-haiku-4.5", exact: true },
  { groupId: "anthropic", pattern: "anthropic/claude-sonnet-4.5", exact: true },
  { groupId: "anthropic", pattern: "anthropic/claude-opus-4.8", exact: true },
  { groupId: "google", pattern: "google/gemini-3.5-flash", exact: true },
  { groupId: "google", pattern: "google/gemini-2.5-pro", exact: true },
  { groupId: "deepseek", pattern: "deepseek/deepseek-v4-pro", exact: true },
  { groupId: "routers", pattern: "openrouter/fusion", exact: true },
  { groupId: "routers", pattern: "openrouter/pareto-code", exact: true },
];

function isFeatured(modelId) {
  const id = String(modelId || "");
  return FEATURED_MATCHERS.some(f => f.exact ? id === f.pattern : id.startsWith(f.pattern));
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. Helpery (display name, group detection, price/status badges)
// ─────────────────────────────────────────────────────────────────────────────

function getSlugOrg(modelId) {
  if (!modelId) return "other";
  const s = String(modelId);
  const idx = s.indexOf("/");
  if (idx < 0) return "other";
  return s.slice(0, idx).toLowerCase();
}

function getGroup(modelId) {
  const slug = getSlugOrg(modelId);
  for (const g of GROUPS) {
    if (g.id !== "other" && g.matchers(slug)) return g;
  }
  return GROUPS[GROUPS.length - 1]; // "other"
}

function displayName(model) {
  if (model.name && !model.name.startsWith("openrouter/")) return model.name;
  const id = String(model.id || "");
  const slash = id.indexOf("/");
  return slash >= 0 ? id.slice(slash + 1) : id;
}

/** USD/M tokenów ważone 3:1 (prompt:completion). Dla OpenRouter. */
function pricePerMillionUsd(pricing) {
  if (!pricing) return null;
  const p = parseFloat(pricing.prompt);
  const c = parseFloat(pricing.completion);
  if (!isFinite(p) && !isFinite(c)) return null;
  const pm = (isFinite(p) ? p : 0) * 1e6;
  const cm = (isFinite(c) ? c : 0) * 1e6;
  return pm * 0.75 + cm * 0.25;
}

function priceBadge(pricing) {
  const v = pricePerMillionUsd(pricing);
  if (v === null) return null;
  if (v <= 0) return { label: "Free", tone: "emerald" };
  if (v < 0.6) return { label: "Bardzo tani", tone: "emerald" };
  if (v < 3) return { label: "Tani", tone: "green" };
  if (v < 15) return { label: "Średni", tone: "amber" };
  return { label: "Drogi", tone: "rose" };
}

function statusBadges(model) {
  const id = String(model.id || "").toLowerCase();
  const desc = String(model.description || "").toLowerCase();
  const out = [];
  if (id.startsWith("~") || id.includes("-latest"))
    out.push({ label: "Latest alias", tone: "indigo" });
  if (id.includes("preview") || id.includes("alpha") || id.includes("beta")
      || desc.includes("preview") || desc.includes("experimental")) {
    out.push({ label: "Preview", tone: "violet" });
  }
  if (id.includes("deprecated") || desc.includes("deprecated")
      || desc.includes("legacy")) {
    out.push({ label: "Deprecated", tone: "stone" });
  }
  if (id.includes(":free")) out.push({ label: "Free tier", tone: "emerald" });
  if (id.includes(":nitro")) out.push({ label: "Nitro", tone: "blue" });
  return out;
}

const TONE = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  green: "bg-green-50 text-green-700 border-green-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  violet: "bg-violet-50 text-violet-700 border-violet-200",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  stone: "bg-stone-100 text-stone-700 border-stone-300",
};

const Badge = ({ tone, label }) => (
  <span className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded border ${TONE[tone] || TONE.stone}`}>
    {label}
  </span>
);

// ─────────────────────────────────────────────────────────────────────────────
// 4. Komponenty wewnętrzne
// ─────────────────────────────────────────────────────────────────────────────

const ModelRow = ({ model, selected, onSelect }) => {
  const price = priceBadge(model.pricing);
  const stats = statusBadges(model);
  const ctx = model.context_length || model.top_provider?.context_length;
  return (
    <button
      type="button"
      onClick={() => onSelect(model.id)}
      data-testid={`openrouter-model-${model.id}`}
      className={`w-full text-left px-2.5 py-2 rounded-md border text-xs transition-colors flex flex-col gap-1 ${
        selected
          ? "border-indigo-400 bg-indigo-50/70"
          : "border-transparent hover:bg-stone-50 hover:border-stone-200"
      }`}
    >
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-stone-800 truncate">{displayName(model)}</div>
          <div className="text-[10px] text-stone-500 truncate">{model.id}</div>
        </div>
        <div className="flex flex-wrap gap-1">
          {price ? <Badge {...price} /> : null}
          {stats.map((s, i) => <Badge key={i} {...s} />)}
        </div>
      </div>
      {ctx ? (
        <div className="text-[10px] text-stone-500">Kontekst: {(ctx / 1000).toFixed(0)}k tokenów</div>
      ) : null}
    </button>
  );
};

function GroupHeader({ group, count, expanded, onToggle }) {
  const { Icon, iconClass, dotClass, label, id } = group;
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={`group-header-${id}`}
      className="w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-stone-50 border border-stone-200 bg-white sticky top-0 z-10"
    >
      {expanded
        ? <ChevronDown className="w-3.5 h-3.5 text-stone-400 shrink-0" />
        : <ChevronRight className="w-3.5 h-3.5 text-stone-400 shrink-0" />}
      <span className={`inline-flex items-center justify-center w-5 h-5 rounded ${dotClass} bg-opacity-15`}>
        <Icon className={`w-3.5 h-3.5 ${iconClass}`} />
      </span>
      <span className="text-xs font-semibold text-stone-800 flex-1 text-left">{label}</span>
      <span className="text-[11px] text-stone-500 font-mono" data-testid={`group-count-${id}`}>
        ({count})
      </span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. Sortowanie modeli w grupie (cena rosnąco, null na końcu)
// ─────────────────────────────────────────────────────────────────────────────

function sortByPrice(list) {
  return [...list].sort((a, b) => {
    const pa = pricePerMillionUsd(a.pricing);
    const pb = pricePerMillionUsd(b.pricing);
    if (pa === null && pb === null) return 0;
    if (pa === null) return 1;
    if (pb === null) return -1;
    return pa - pb;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. Główny komponent
// ─────────────────────────────────────────────────────────────────────────────

export default function OpenRouterModelSelector({ models = [], value = "", onChange, previousModel = "" }) {
  const [search, setSearch] = useState("");
  const [onlyRecommended, setOnlyRecommended] = useState(true);
  // Mobile-first: domyślnie WSZYSTKO zwinięte. Featured rozwinięte tylko gdy
  // toggle "Tylko polecane" wyłączony i nie ma search-a.
  const [expanded, setExpanded] = useState({ featured: true });

  // ── Polecane: filtr nad pełną listą ────────────────────────────────────────
  const featured = useMemo(() => {
    // Zachowaj kolejność FEATURED_MATCHERS (najpierw OpenAI, potem Anthropic, …).
    const byId = new Map(models.map(m => [m.id, m]));
    const out = [];
    for (const f of FEATURED_MATCHERS) {
      if (f.exact) {
        const m = byId.get(f.pattern);
        if (m) out.push(m);
      } else {
        for (const m of models) {
          if (m.id.startsWith(f.pattern) && !out.includes(m)) {
            out.push(m);
            break;
          }
        }
      }
    }
    return out;
  }, [models]);

  // ── Grupowanie modeli po producentach ──────────────────────────────────────
  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const g of GROUPS) buckets.set(g.id, []);
    for (const m of models) {
      const g = getGroup(m.id);
      buckets.get(g.id).push(m);
    }
    // Sortuj zawartość każdej grupy po cenie rosnąco.
    for (const [k, list] of buckets) buckets.set(k, sortByPrice(list));
    return buckets;
  }, [models]);

  // ── Wyszukiwanie po name + slug + nazwie producenta ────────────────────────
  const q = search.trim().toLowerCase();
  const matchesSearch = (m) => {
    if (!q) return true;
    const id = String(m.id || "").toLowerCase();
    const name = String(m.name || "").toLowerCase();
    if (id.includes(q) || name.includes(q)) return true;
    const groupLabel = getGroup(m.id).label.toLowerCase();
    return groupLabel.includes(q);
  };

  // Filtruj grupy po search.
  const filteredGroups = useMemo(() => {
    const out = new Map();
    for (const g of GROUPS) {
      out.set(g.id, (grouped.get(g.id) || []).filter(matchesSearch));
    }
    return out;
  }, [grouped, q]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-expand grup z trafieniami gdy aktywny search.
  useEffect(() => {
    if (!q) return;
    setExpanded(prev => {
      const next = { ...prev };
      let changed = false;
      for (const g of GROUPS) {
        const cnt = (filteredGroups.get(g.id) || []).length;
        if (cnt > 0 && !next[g.id]) {
          next[g.id] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [q, filteredGroups]);

  // Auto-pick modelu: jeśli value nieistnieje, wybierz featured → previous → pierwszy.
  useEffect(() => {
    if (!models || models.length === 0) return;
    const exists = models.some(m => m.id === value);
    if (exists) return;
    if (previousModel && models.some(m => m.id === previousModel)) {
      onChange?.(previousModel);
      return;
    }
    if (featured.length > 0) {
      onChange?.(featured[0].id);
      return;
    }
    const first = models[0];
    if (first) onChange?.(first.id);
  }, [models, value, previousModel, featured, onChange]);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const totalFiltered = useMemo(() => {
    if (!q) return models.length;
    let n = 0;
    for (const [, list] of filteredGroups) n += list.length;
    return n;
  }, [filteredGroups, q, models.length]);

  return (
    <div className="space-y-3" data-testid="openrouter-model-selector">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
          <Input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Szukaj: nazwa, slug lub producent…"
            className="pl-7 h-8 text-xs"
            data-testid="openrouter-search"
          />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Switch
            id="only-recommended"
            checked={onlyRecommended}
            onCheckedChange={setOnlyRecommended}
            data-testid="openrouter-only-recommended"
          />
          <Label htmlFor="only-recommended" className="text-xs cursor-pointer flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-amber-500" />
            Tylko polecane
          </Label>
        </div>
      </div>

      {/* Aktualny wybór */}
      {value ? (
        <div className="text-[10px] text-stone-500 px-1" data-testid="openrouter-current">
          Wybrany: <code className="bg-stone-100 px-1 py-0.5 rounded text-stone-700">{value}</code>
        </div>
      ) : null}

      {/* Lista */}
      <div className="max-h-[460px] overflow-y-auto pr-1 space-y-2 border border-stone-200 rounded-md p-2 bg-white">
        {/* ⭐ Polecane */}
        {(!q || featured.some(matchesSearch)) ? (
          <section data-testid="group-featured">
            <button
              type="button"
              onClick={() => toggle("featured")}
              data-testid="group-header-featured"
              className="w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-amber-50 border border-amber-200 bg-amber-50/40 sticky top-0 z-10"
            >
              {expanded.featured
                ? <ChevronDown className="w-3.5 h-3.5 text-amber-700 shrink-0" />
                : <ChevronRight className="w-3.5 h-3.5 text-amber-700 shrink-0" />}
              <Star className="w-4 h-4 fill-amber-400 stroke-amber-600" />
              <span className="text-xs font-semibold text-amber-800 flex-1 text-left">Polecane</span>
              <span className="text-[11px] text-amber-700 font-mono" data-testid="group-count-featured">
                ({featured.length})
              </span>
            </button>
            {expanded.featured ? (
              <div className="space-y-1 mt-1 px-1">
                {featured.filter(matchesSearch).map(m => (
                  <ModelRow key={m.id} model={m} selected={m.id === value} onSelect={onChange} />
                ))}
                {featured.length === 0 ? (
                  <div className="text-xs text-stone-500 py-3 text-center">
                    Lista polecanych jest pusta — brak dopasowanych modeli w aktualnym zestawie OpenRouter.
                  </div>
                ) : null}
              </div>
            ) : null}
          </section>
        ) : null}

        {/* Pozostałe grupy — pokazywane tylko gdy „Tylko polecane" wyłączone */}
        {!onlyRecommended ? GROUPS.map(g => {
          const list = filteredGroups.get(g.id) || [];
          // Gdy aktywny search i grupa pusta — pomiń. Bez searcha — pokaż nagłówek nawet jeśli pusta? Pomijamy puste grupy zawsze (mniejszy noise).
          if (list.length === 0) return null;
          const isOpen = !!expanded[g.id];
          return (
            <section key={g.id} data-testid={`group-${g.id}`}>
              <GroupHeader group={g} count={list.length} expanded={isOpen} onToggle={() => toggle(g.id)} />
              {isOpen ? (
                <div className="space-y-1 mt-1 px-1" data-testid={`group-body-${g.id}`}>
                  {list.map(m => (
                    <ModelRow key={m.id} model={m} selected={m.id === value} onSelect={onChange} />
                  ))}
                </div>
              ) : null}
            </section>
          );
        }) : null}

        {/* Empty state */}
        {q && totalFiltered === 0 && (!featured.some(matchesSearch)) ? (
          <div className="text-xs text-stone-500 py-6 text-center" data-testid="openrouter-no-results">
            Nic nie znaleziono dla &bdquo;{search}&rdquo;.
          </div>
        ) : null}
      </div>

      <div className="text-[10px] text-stone-400 px-1 flex flex-wrap gap-x-3" data-testid="openrouter-footer">
        <span>{models.length} modeli tekstowych</span>
        <span>{featured.length} polecanych</span>
        {q ? <span>{totalFiltered} wynik(ów) wyszukiwania</span> : null}
      </div>
    </div>
  );
}
